import axios from "axios";
import { API_URL_ANGULAR, API_URL } from "../utils/Constant";
import {
  normalizeCustomerSubmissionApiRow,
  normalizeCustomerSampleDataApiRow,
} from "../Containers/Components/Dashboard/Helper";

export const fetchCustomers = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL_ANGULAR}/customers/`,
      {
        signal,
        params,
      });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    const message = "Failed to fetch customers";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

export const fetchAllCustomers = async () => {
  try {
    const response = await axios.get(`${API_URL}/customerinfo/?search=&status=1`);
    const data = await response.data;
    return { success: true, ...data };
  } catch (e) {
    return { success: false, message: "Failed to load company names for search." };
  }
};

export const fetchCustomerInfoList = async (params = {}, signal = null) => {
  try {
    const {
      search = "",
      status = 1,
      customer_id: customerId = "",
    } = params;
    const response = await axios.get(`${API_URL}/customerinfo/`, {
      signal,
      params: {
        search,
        status,
        ...(customerId ? { customer_id: customerId } : {}),
      },
    });
    const data = await response.data;
    return { success: true, ...data };
  } catch (error) {
    return {
      success: false,
      message: "Failed to fetch customers.",
      canceled: error.code === "ERR_CANCELED",
    };
  }
};

export const fetchSamplesFromCoc = async (parameters, signal = null) => {
  try {
    const {
      coc_id,
      wo_status,
    } = parameters;
    const response = await axios.get(`${API_URL_ANGULAR}/samplesinfo/?coc_id=${coc_id}&wo_status=${wo_status}&test_status=true`,
      {
        signal,
      });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    const message = "Failed to fetch Samples";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

export const fetchCocCustomerById = async (payload, signal = null) => {
  try {
    const params = {
      customer_id: payload.customer_id,
      date_range: payload.date_range,
      wo_status: payload.wo_status !== undefined ? payload.wo_status : false,
      retest: payload.retest ? payload.retest : null,
    };

    if (payload.sample_id) {
      params.sample_id = payload.sample_id;
    } else if (payload.sample_type) {
      params.sample_type = payload.sample_type;
    }
    const response = await axios.get(`${API_URL_ANGULAR}/getcustomercocs/`, { params, signal });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    const message = "Failed to fetch COC customer details";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

export const fetchCustomerWos = async (payload, signal = null) => {
  try {
    const params = {
      customer_id: payload.customer_id,
      date_range: payload.date_range,
      retest: payload.retest ? payload.retest : null,
    };
    if (payload.sample_id) {
      params.sample_id = payload.sample_id;
    } else if (payload.sample_type) {
      params.sample_type = payload.sample_type;
    }
    const response = await axios.get(`${API_URL_ANGULAR}/getcustomerwos/`, { params, signal });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    const message = "Failed to fetch Workorders details";
    return {
      success: false,
      message,
      status_code: e?.response?.status ?? 400,
      error_message: e?.message ?? "Fail",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * Fetches activity details based on the provided date and stage filter.
 * @param {object} payload - An object containing date and stage_filter properties.
 * @param {AbortSignal} [signal=null] - An AbortSignal to cancel the request.
 * @return {object} An object containing the API response and a success flag.
 */
export const fetchStagesActivity = async (payload) => {
  try {
    const params = {
      date: payload.date,
      stage_filter: payload.stage_filter,
    };
    const response = await axios.get(`${API_URL}/get_stages_logs/`, { params });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
    };
  }
};

/**
 * Fetches activity details based on the provided date, activity type and search
 * @param {object} payload - An object containing date, update_type, search
 * @param {AbortSignal} [signal=null] - An AbortSignal to cancel the request.
 * @return {object} An object containing the API response and a success flag.
 */
export const fetchActivity = async (payload, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/get_activity_log/`, { params: payload, signal });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (e) {
    return {
      success: false,
      message: e.response?.data?.message || "Something went wrong.",
      canceled: e.code === "ERR_CANCELED",
    };
  }
};

/**
 * GET /customer-sample-data — rows for dashboard Sample & Test tabs.
 *
 * @param {object} payload
 * @param {number} payload.customer_pk - Primary key (same as dashboard `customerId` / customers row `id`)
 * @param {string} payload.date_range - "MM/DD/YYYY-MM/DD/YYYY"
 * @param {string} [payload.sample_id] - Optional search
 * @param {string} [payload.sample_type] - Optional search
 * @param {boolean} [payload.retest] - When true, only retests
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, result?: object[], canceled?: boolean, message?: string }>}
 */
export const fetchCustomerSampleData = async (payload, signal = null) => {
  const customerPrimaryKey = payload.customer_pk ?? payload.customer_id;
  const params = {
    customer_pk: customerPrimaryKey,
    date_range: payload.date_range,
  };

  if (payload.sample_id) {
    params.sample_id = payload.sample_id;
  } else if (payload.sample_type) {
    params.sample_type = payload.sample_type;
  }

  if (payload.retest) {
    params.retest = payload.retest;
  }

  try {
    const response = await axios.get(`${API_URL}/customer_sample_data/`, { params, signal });

    if (signal?.aborted) {
      return { success: false, canceled: true };
    }

    const responseBody = response.data;
    if (responseBody.success === true && Array.isArray(responseBody.result)) {
      const mappedResult = responseBody.result.map((row) => normalizeCustomerSampleDataApiRow(row));
      return { success: true, result: mappedResult };
    }

    return {
      success: false,
      message: responseBody?.message || "Unexpected response from customer sample data",
      result: [],
    };
  } catch (error) {
    if (error.code === "ERR_CANCELED") {
      return { success: false, canceled: true, message: error.response?.data?.message || "Something went wrong." };
    }

    return {
      success: false,
      message: error.response?.data?.message || "Something went wrong.",
      result: [],
    };
  }
};

/**
 * Modal detail rows for Sample / Test tabs — GET /customer-submission-data.
 *
 * @param {object} payload
 * @param {number|string} payload.customer_pk - Dashboard customer primary key (same as selected row `id`)
 * @param {number|string} payload.test_category_pk - Test category primary key (not display name)
 * @param {number|string} [payload.test_name_pk] - Test primary key for single-test modal; omit or "" for category-wide (Sample / category modals)
 * @param {string} payload.date_range - "MM/DD/YYYY-MM/DD/YYYY"
 * @param {string} [payload.sample_id] - optional filter when provided with non-empty value
 * @param {string} [payload.sample_type] - optional filter when provided with non-empty value
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ success: boolean, result?: object[], canceled?: boolean, message?: string }>}
 */
export const fetchCustomerSubmissionData = async (payload, signal = null) => {
  const params = {
    ...payload,
    customer_pk: payload.customer_pk ?? payload.customer_id,
    test_category_pk: payload.test_category_pk,
    test_name_pk: payload.test_name_pk ?? "",
    date_range: payload.date_range,
    retest: payload.retest ? payload.retest : null,
  };

  try {
    const response = await axios.get(`${API_URL}/customer_submission_data/`, {
      params,
      signal,
    });

    if (signal?.aborted) {
      return { success: false, canceled: true };
    }

    const responseBody = response.data;
    if (responseBody?.success === true && Array.isArray(responseBody.result)) {
      const mappedResult = responseBody.result.map((row) => normalizeCustomerSubmissionApiRow(row));
      return { success: true, result: mappedResult };
    }

    return {
      success: false,
      message: responseBody?.message || "Unexpected response from customer submission data",
      result: [],
    };
  } catch (error) {
    if (error.code === "ERR_CANCELED") {
      return { success: false, canceled: true };
    }

    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to fetch customer submission data",
      result: [],
    };
  }
};

/**
 * Customer submission analytics (samples, permissions, ESV / other submission counts).
 * GET /customer-submission-info
 *
 * @param {object} payload
 * @param {number} payload.customer_id
 * @param {string} payload.date_range
 * @param {string} [payload.sample_id]
 * @param {string} [payload.sample_type]
 * @param {boolean} [payload.retest]
 * @param {AbortSignal} [signal]
 */
export const fetchCustomerSubmissionInfo = async (payload, signal = null) => {
  const params = {
    customer_pk: payload.customer_id,
    date_range: payload.date_range,
  };

  if (payload.sample_id) {
    params.sample_id = payload.sample_id;
  } else if (payload.sample_type) {
    params.sample_type = payload.sample_type;
  }

  if (payload.retest) {
    params.retest = payload.retest;
  }

  try {
    const response = await axios.get(`${API_URL}/customer_submission_info/`, { params, signal });

    if (signal?.aborted) {
      return { success: false, canceled: true };
    }

    const data = response.data;
    if (!data || data.success === false) {
      return {
        success: false,
        message: data?.message || "Failed to fetch customer submission info",
      };
    }

    return { success: true, ...data };
  } catch (error) {
    if (error.code === "ERR_CANCELED") {
      return { success: false, canceled: true };
    }
    return {
      success: false,
      message: error.response?.data?.message || error.message || "Something went wrong.",
    };
  }
};

export const fetchEsvRetestActions = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/esv_retest_actions/`, { params, signal });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (error) {
    if (error.code === "ERR_CANCELED") {
      return { success: false, canceled: true };
    }
    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to fetch ESV retest actions",
    };
  }
};

export const markEsvRetestActionRead = async (payload) => {
  try {
    const response = await axios.patch(`${API_URL}/esv_retest_actions_mark_read/`, payload);
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to mark action as read",
    };
  }
};

export const updateEsvRetestActionDataOps = async (payload) => {
  try {
    const response = await axios.patch(`${API_URL}/esv_retest_actions_mark_read/`, payload);
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to save Data Ops update",
    };
  }
};

/**
 * GET /qc_records_ml/ — ML Routing dashboard table (read-only).
 * @param {{ search?: string, customer_id?: string, status?: 'pass'|'fail'|'pending', date_range?: string }} params
 */
export const fetchMlRoutingRecords = async (params, signal = null) => {
  try {
    const response = await axios.get(`${API_URL}/qc_records_ml/`, { params, signal });
    const apiResponse = await response.data;
    return { success: true, ...apiResponse };
  } catch (error) {
    if (error.code === "ERR_CANCELED") {
      return { success: false, canceled: true };
    }
    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to fetch ML routing records",
    };
  }
};
